import React, { useCallback, useEffect, useState } from 'react';
import { Tree as AntdTree } from 'antd';
import { DataNode as AntDataNode, TreeProps as AntdTreeProps } from 'antd/lib/tree';
import classNames from 'classnames';
import { TreeNodeNormal } from 'antd/lib/tree/Tree';
import _chain from 'lodash/flatten';
import _map from 'lodash/map';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { BringgInput } from '../index';

const { TreeNode } = AntdTree;
export type DataNode = AntDataNode;

interface Translations {
	placeholder: string;
}

interface Props extends AntdTreeProps {
	withHeader?: boolean;
	additionalHeaderElement?: React.ReactNode;
	search?: {
		filterBy?: string;
		translations: Translations;
	};
	expandAllText?: string;
	collapseAllText?: string;
}

const getAllKeys = (treeData: Array<TreeNodeNormal>): string[] => {
	return _chain(treeData)
		.map(data => getAllKeysRecursive(data))
		.flat(1);
};

const getAllKeysRecursive = (treeNode: TreeNodeNormal): string[] => {
	const childrenKeys: string[] = _map(treeNode.children, getAllKeysRecursive).flat(1);
	return [treeNode.key.toString(), ...childrenKeys];
};

const Tree = ({
	className,
	withHeader,
	additionalHeaderElement,
	search,
	expandAllText = 'Expand All',
	collapseAllText = 'Collapse All',
	expandedKeys,
	treeData = [],
	...treeProps
}: Props) => {
	const combinedClassName = classNames('bringg-tree', className);
	const [expandedItems, setExpandedItems] = useState([]);
	const [displayedTreeData, setDisplayedTreeData] = useState(treeData);

	useEffect(() => {
		setExpandedItems(expandedKeys || []);
	}, [expandedKeys]);

	const collapse = useCallback(() => {
		setExpandedItems([]);
	}, [expandedItems]);

	const expand = useCallback(() => {
		const keys = getAllKeys(treeData);
		setExpandedItems(keys);
	}, [expandedItems]);

	const [searchString, setSearchString] = useState<string>('');

	useEffect(() => {
		const recursiveFilter = (arr: TreeNodeNormal[], filterBy: string, match: string) => {
			return arr.filter(el => {
				const left = el[filterBy].toString().toLowerCase().includes(match.toLowerCase());

				if (!left && el.children?.length) {
					return recursiveFilter(el.children, filterBy, match)?.length > 0;
				}

				return left;
			});
		};

		if (search) {
			setDisplayedTreeData(recursiveFilter(treeData, search.filterBy ?? 'title', searchString));
		}
	}, [treeData, searchString, setDisplayedTreeData, search?.filterBy]);

	const onChange = useCallback(
		event => {
			setSearchString(event.target.value);
		},
		[setSearchString]
	);

	return (
		<div className="tree-wrapper">
			{withHeader && (
				<div className="button-container">
					<span className="tree-expand-button" onClick={expand}>
						{expandAllText}
					</span>
					<span className="tree-collapse-button" onClick={collapse}>
						{collapseAllText}
					</span>
					{additionalHeaderElement}
				</div>
			)}
			{search && (
				<>
					<div className="search-input">
						<BringgInput
							placeholder={search.translations.placeholder ?? 'Search'}
							value={searchString}
							onChange={onChange}
							prefix={<BringgIcon iconName={BringgFontIcons.Search} />}
						/>
					</div>
					<div className="search-input-divider" />
				</>
			)}
			<AntdTree
				className={combinedClassName}
				expandedKeys={expandedItems}
				treeData={displayedTreeData}
				onExpand={setExpandedItems}
				{...treeProps}
			/>
		</div>
	);
};

export default Tree;
export { Tree, TreeNode };
